/**
 * Hook that composes the `schema` map a `FormulaDisplay` needs to render a
 * validation-rule formula over a target entity type.
 *
 * A validation-rule formula references two kinds of tokens:
 *   - the target entity type's own properties (`ahoy_amount > 1000`)
 *   - an outgoing association traversed to a property on its target
 *     (`ahoy_deal_to_deal_pipeline_stage.ahoy_order`)
 *
 * The first kind resolves from the target type's own property schema. The
 * second needs the association metadata (label + target entity type) *and* the
 * property schema of the association's target entity type so the nested
 * property chip resolves. This hook composes all three into the
 * `Record<string, FormulaSchemaEntry>` shape FormulaDisplay consumes:
 *   - property entries keyed by `property_type_name`
 *   - association entries keyed by `association_type_name`, each carrying its
 *     label, target entity type, and that target type's property schema
 *
 * Unresolved tokens degrade to plain text in FormulaDisplay, so a still-loading
 * or partial map is safe to pass — chips fill in as fetches resolve.
 */

import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

import type {
  FormulaAssociationSchema,
  FormulaSchemaEntry,
} from '../components/properties/FormulaDisplay';
import type { EntityTypeName } from '../core/types';
import { useAssociationTypes } from './useAssociationTypes';
import { useEntityTypeSchema } from './useEntityTypeSchema';
import { fetchPropertyTypes } from './useFilterSchema';
import type { PropertyTypeSchema } from './usePropertySchema';
import { usePropertySchema } from './usePropertySchema';

const SCHEMA_STALE_TIME = 5 * 60 * 1000;

const EMPTY_KINDS: string[] = [];

/**
 * Compose the FormulaDisplay `schema` map for a formula authored over
 * `targetEntityTypeName`. Returns `undefined` when no target type is supplied
 * so callers can spread it straight into an optional `schema` prop.
 *
 * @param orgId - Organization ID
 * @param targetEntityTypeName - The formula's target entity type (empty string
 *   disables all fetches, matching the underlying hooks' `enabled` guards)
 */
export function useFormulaTargetSchema(
  orgId: number,
  targetEntityTypeName: string
): Record<string, FormulaSchemaEntry> | undefined {
  const { schema: ownPropertySchema } = usePropertySchema(
    orgId,
    targetEntityTypeName
  );

  const { associationTypes } = useAssociationTypes(orgId || undefined);

  // The target entity type's ontology kinds (e.g. ['deal'], ['person']). Needed
  // to resolve kind-based outgoing associations, whose `source_entity_type_name`
  // is empty but which apply to every type sharing `source_entity_kind_name`.
  const { schema: targetTypeSchema } = useEntityTypeSchema(
    orgId || undefined,
    (targetEntityTypeName || undefined) as EntityTypeName | undefined
  );
  const targetEntityKinds = targetTypeSchema?.entity_kind ?? EMPTY_KINDS;

  // Associations whose source is the formula's target entity type — these are
  // the only traversals a formula over that type can express. An association is
  // outgoing when either (a) its source TYPE matches the target type, or (b) it
  // is kind-based (empty source type) and its source KIND is one of the target
  // entity's kinds — but not when this type is explicitly excluded from that
  // kind-based resolution.
  const outgoingAssociations = useMemo(
    () =>
      targetEntityTypeName
        ? associationTypes.filter(at => {
            if (at.source_entity_type_name === targetEntityTypeName) {
              return true;
            }
            if (
              at.source_entity_kind_name &&
              targetEntityKinds.includes(at.source_entity_kind_name) &&
              !(at.source_excluded_entity_type_names ?? []).includes(
                targetEntityTypeName
              )
            ) {
              return true;
            }
            return false;
          })
        : [],
    [associationTypes, targetEntityTypeName, targetEntityKinds]
  );

  // Distinct target entity types across those associations — fetch each type's
  // property schema once (deduped), so a nested `assoc.prop` chip can resolve.
  const associationTargetTypeNames = useMemo(
    () =>
      Array.from(
        new Set(outgoingAssociations.map(at => at.target_entity_type_name))
      ),
    [outgoingAssociations]
  );

  const targetSchemaQueries = useQueries({
    queries: associationTargetTypeNames.map(typeName => ({
      queryKey: ['propertySchema', orgId, typeName],
      queryFn: () => fetchPropertyTypes(orgId, typeName),
      enabled: !!orgId && !!typeName,
      staleTime: SCHEMA_STALE_TIME,
    })),
  });

  // Map each association target entity type -> its property schema map.
  const targetSchemaByType = useMemo(() => {
    const byType: Record<string, Record<string, PropertyTypeSchema>> = {};
    associationTargetTypeNames.forEach((typeName, index) => {
      const data = targetSchemaQueries[index]?.data;
      if (!data) {
        return;
      }
      const map: Record<string, PropertyTypeSchema> = {};
      for (const pt of data) {
        map[pt.property_type_name] = pt;
      }
      byType[typeName] = map;
    });
    return byType;
  }, [associationTargetTypeNames, targetSchemaQueries]);

  return useMemo(() => {
    if (!targetEntityTypeName) {
      return undefined;
    }

    const composed: Record<string, FormulaSchemaEntry> = {
      ...ownPropertySchema,
    };

    for (const at of outgoingAssociations) {
      const associationEntry: FormulaAssociationSchema = {
        label: at.label,
        target_entity_type_name: at.target_entity_type_name,
        properties: targetSchemaByType[at.target_entity_type_name],
      };
      composed[at.association_type_name] = associationEntry;
    }

    return composed;
  }, [
    targetEntityTypeName,
    ownPropertySchema,
    outgoingAssociations,
    targetSchemaByType,
  ]);
}
